import { ChapterService } from '../services/chapter.service';
import { ChapterNoteService } from '../services/chapternote.service';

/**
 * Handles updating chapter name
 * @param editableChapterName The new name for the chapter
 * @param selectedChapterId The ID of the chapter to update
 * @param contentData The current content data
 * @param setIsSubmitting State setter for submission status
 * @param setChapters State setter for chapters
 * @param setContentData State setter for content data
 * @param setIsEditingChapterName State setter for editing chapter name flag
 */
export const updateChapterName = async (
  editableChapterName: string,
  selectedChapterId: string,
  contentData: any,
  chapters: any[],
  setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>,
  setChapters: React.Dispatch<React.SetStateAction<any[]>>,
  setContentData: React.Dispatch<React.SetStateAction<any>>,
  setIsEditingChapterName: React.Dispatch<React.SetStateAction<boolean>>
): Promise<void> => {
  if (!editableChapterName.trim()) {
    alert('Chapter name cannot be empty');
    return;
  }
  
  if (!selectedChapterId) {
    alert('No chapter selected');
    return;
  }
  
  const chapterId = parseInt(selectedChapterId, 10);
  
  try {
    setIsSubmitting(true);
    
    const updatedChapter = await ChapterService.updateChapter(chapterId, {
      name: editableChapterName.trim()
    });
    
    // Update local state
    setChapters(chapters.map(chapter => 
      chapter.id === chapterId ? updatedChapter : chapter
    ));
    
    // Update content data
    if (contentData) {
      setContentData({
        ...contentData,
        chapter: updatedChapter.name
      });
    }
    
    setIsEditingChapterName(false);
    
    // Show success message
    alert(`Chapter name was successfully updated to "${updatedChapter.name}".`);
  } catch (err) {
    console.error('Error updating chapter name:', err);
    alert('Failed to update chapter name. Please try again later.');
  } finally {
    setIsSubmitting(false);
  }
};

/**
 * Handles saving processed JSON data
 * @param contentData The content data to save
 * @param editableChapterName The editable chapter name
 * @param subjectId The ID of the subject
 * @param setIsSubmitting State setter for submission status
 * @param setSelectedFile State setter for selected file
 * @param setChapters State setter for chapters
 * @param setSelectedChapterId State setter for selected chapter ID
 * @param setShowContent State setter for show content flag
 * @param setContentData State setter for content data
 * @param onSelectChapter Callback for chapter selection
 * @param loadChapterContentFn Function to load chapter content
 */
export const saveProcessedData = async (
  contentData: any,
  editableChapterName: string,
  subjectId: number | null,
  setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>,
  setSelectedFile: React.Dispatch<React.SetStateAction<File | null>>,
  setChapters: React.Dispatch<React.SetStateAction<any[]>>,
  setSelectedChapterId: React.Dispatch<React.SetStateAction<string>>,
  setShowContent: React.Dispatch<React.SetStateAction<boolean>>,
  setContentData: React.Dispatch<React.SetStateAction<any>>,
  onSelectChapter: (chapterId: number) => void,
  loadChapterContentFn: (chapterId: number) => Promise<void>
): Promise<void> => {
  if (!contentData) {
    alert('No data to save');
    return;
  }
  
  // Validate data
  if (editableChapterName.trim() === '') {
    alert('Chapter name cannot be empty');
    return;
  }

  // Update chapter name in content data
  const dataToSave = {
    ...contentData,
    chapter: editableChapterName
  };
  
  // Validate notes and questions
  for (const note of dataToSave.notes) {
    if (!note.content || note.content.trim() === '') {
      alert('Note content cannot be empty');
      return;
    }
    
    if (note.questions) {
      for (const q of note.questions) {
        if (!q.question || !q.answer || q.question.trim() === '' || q.answer.trim() === '') {
          alert('Question and answer fields cannot be empty');
          return;
        }
      }
    }
  }
  
  if (!subjectId) {
    alert('Subject ID is required');
    return;
  }

  const chapterNotesData = {
    chapter: dataToSave.chapter,
    subject_id: subjectId, 
    notes: dataToSave.notes
  };
  
  try {
    setIsSubmitting(true);
    
    // Save chapter with notes and questions
    const result = await ChapterNoteService.createChapterNotes(chapterNotesData);
    
    // Success message
    alert(`Successfully saved chapter "${dataToSave.chapter}" with ${dataToSave.notes.length} notes.`);
    
    // Reset file selection
    setSelectedFile(null);
    
    // Refresh chapters list
    const chaptersData = await ChapterService.getChaptersBySubjectId(subjectId);
    setChapters(chaptersData);
    
    // Switch to existing chapter mode for the newly created chapter
    if (result && result.chapter_id) {
      setSelectedChapterId(String(result.chapter_id));
      // Don't trigger parent's handler since we're handling display ourselves
      onSelectChapter(0);
      loadChapterContentFn(result.chapter_id);
    } else {
      // Reset content display if we couldn't get the new chapter ID
      setShowContent(false);
      setContentData(null);
    }
  } catch (error) {
    console.error('Error saving data:', error);
    alert(`Failed to save data: ${error instanceof Error ? error.message : 'Unknown error'}`);
  } finally {
    setIsSubmitting(false);
  }
};

/**
 * Handles closing content view
 * @param isNewContent Flag indicating if this is new content
 * @param setShowContent State setter for show content flag
 * @param setContentData State setter for content data
 * @param setSelectedFile State setter for selected file
 */
export const closeContent = (
  isNewContent: boolean,
  setShowContent: React.Dispatch<React.SetStateAction<boolean>>,
  setContentData: React.Dispatch<React.SetStateAction<any>>,
  setSelectedFile: React.Dispatch<React.SetStateAction<File | null>>
): void => {
  if (isNewContent && window.confirm('Are you sure you want to discard this content?')) {
    setShowContent(false);
    setContentData(null);
    setSelectedFile(null);
  } else if (!isNewContent) {
    setShowContent(false);
    setContentData(null);
  }
};